import { NavigateFunction } from "react-router-dom";
import { ApolloError } from "@apollo/client";
import jwtDecode from "jwt-decode";

import { OTP_DELIVERY_CHANNEL } from "constants/constants";
import { PUBLIC_ROUTER } from "constants/routes";
import { isAppRunningInDev } from "utils/utils";
import { IAuthLocalStore } from "interfaces/localStore/authLocalStore.interface";
import {
  getFromSessionStorage,
  getSessionStorageString,
  saveToSessionStorage,
  setSessionStorageString,
} from "./sessionStorage";
import { LOCAL_STORAGE_AUTH_KEY, SESSION_STORAGE_IS_DATA_AND_PRIVACY_ACCEPTED } from "constants/localStore";
import { IDecodeIdToken } from "../interfaces/login/loginMethod.interface";
import { HTTP_STATUS_CODE } from "constants/authorization";
import { clearOfflineStorage } from "./offlineStorage";
import { IVerifyOTPResponseDTO } from "dto/login/verifyOTP.dto";
import { enqueueSnackbar } from "notistack";
import { Dispatch } from "react";
import { AnyAction } from "@reduxjs/toolkit";
import {
  actionAuthenticatingUser,
  actionUnauthorizeUser,
  actionUpdateAuthorizationReducer,
} from "redux/slices/authorizationSlice";
import { getProfileToLocalDB } from "./profile";
import { logOutAppFlavor, showLoadProductsFromLocalDB } from "./appFlavors";
import { IAccountRoles, IAccountTypes } from "interfaces/account/accountProfile.interface";
import { clearDefaultLocalForage } from "storage/localForage/locaForage.config";
import { getIsPreSignUpFlowFlag, setPreSignUpFlow } from "./signUp";

export const isOTPFromCall = (otpDeliveryChannel: string) => {
  return otpDeliveryChannel === OTP_DELIVERY_CHANNEL.call;
};

export const logoutUser: (arg: { navigate?: NavigateFunction; isReloadPage?: boolean }) => void = async ({
  navigate,
  isReloadPage = true,
} = {}) => {
  if (isAppRunningInDev()) {
    console.log("*** call utils/auth.ts.logoutUser");
  }

  logOutAppFlavor();
  await clearDefaultLocalForage();
  clearOfflineStorage({
    clearLocalStorage: true,
    clearSessionStorage: true,
  });

  if (!!navigate) {
    navigate(PUBLIC_ROUTER.LOGIN.path);
  }

  if (isReloadPage) {
    if (isAppRunningInDev()) {
      console.log("*** call utils/auth.ts.logoutUser is reloading the page...");
    }

    window.location.reload();
  }
};

export const getFMCGAccountID = () => {
  const profile = getProfileToLocalDB();

  let fmcgAccountID: string = profile.fmcg_account_id || "";

  return fmcgAccountID;
};

export const getAccessTokenId = () => {
  const authStore = getStorageAuthKeys();

  let accessTokenID: string | undefined | null = "";

  if (authStore) {
    const { access_token = "" } = authStore;
    accessTokenID = access_token;
  }

  return accessTokenID;
};

export const isGraphQLClientUnauthorized = (apolloError: ApolloError) => {
  const { graphQLErrors } = apolloError;

  const isUnauthenticated = (graphQLErrors || []).some(gqlErr => {
    const { code = "" } = gqlErr as any;

    return code === "UNAUTHENTICATED";
  });

  const isRetroUnauthenticated = (graphQLErrors || []).some(gqlErr => {
    const { code = "" } = gqlErr as any;

    return code === "RETRO_UNAUTHENTICATED";
  });

  return { isUnauthenticated, isRetroUnauthenticated };
};

export const isRESTAPIUnAuthorized = (httpStatusCode: number) => {
  const { FORBIDDEN, UNAUTHORIZED } = HTTP_STATUS_CODE;

  return httpStatusCode === UNAUTHORIZED || httpStatusCode === FORBIDDEN;
};

export const getRefreshTokenKey = () => {
  const authStore = getStorageAuthKeys();

  return authStore?.refresh_token ? authStore?.refresh_token : "";
};

export const decodeIdToken = async ({ idToken }: { idToken: string }): Promise<IDecodeIdToken | null> => {
  try {
    const decodedIdToken = (await jwtDecode(idToken)) as IDecodeIdToken;
    return decodedIdToken;
  } catch (error) {
    console.log({ error });

    return null;
  }
};

export const isDataAndPrivacyAccepted = () => {
  return getSessionStorageString(SESSION_STORAGE_IS_DATA_AND_PRIVACY_ACCEPTED, "") === "true" || true;
};

export const setDataAndPrivacyAccepted = (isAccepted: boolean) => {
  return setSessionStorageString(SESSION_STORAGE_IS_DATA_AND_PRIVACY_ACCEPTED, `${isAccepted}`);
};

export const reloadUserLocalSessionStorage: (arg: { navigate?: NavigateFunction; isReloadPage?: boolean }) => void = ({
  navigate,
  isReloadPage = true,
} = {}) => {
  if (isAppRunningInDev()) {
    console.log("*** call utils/auth.ts.reloadUserLocalSessionStorage");
  }

  // presist data even after refresh or remount
  const isTermsAccepted = getSessionStorageString(SESSION_STORAGE_IS_DATA_AND_PRIVACY_ACCEPTED, "");
  const isPreSignUpFlag = getIsPreSignUpFlowFlag();

  clearOfflineStorage({
    clearLocalStorage: true,
    clearSessionStorage: true,
  });

  if (!!isTermsAccepted) {
    setDataAndPrivacyAccepted(true);
  }

  if (isPreSignUpFlag) {
    setPreSignUpFlow(true);
  }

  if (!!navigate) {
    navigate(PUBLIC_ROUTER.LOGIN.path);
  }

  if (isReloadPage) {
    if (isAppRunningInDev()) {
      console.log("*** call utils/auth.ts.reloadUserLocalSessionStorage is reloading the page...");
    }

    window.location.reload();
  }
};

export const saveAppAuthorizationToken = async (response: IVerifyOTPResponseDTO) => {
  const { access_token, refresh_token } = response;
  const decodedAccessToken = await getDecodedAccessToken(access_token || "");
  const accountType = decodedAccessToken?.type || ("" as IAccountTypes);
  const isAllowed = isAccountTypeAllowed(accountType);

  if (isAllowed) {
    const authStore: IAuthLocalStore = {
      access_token,
      refresh_token,
      store_username: "",
      user_type: accountType,
    };

    setStorageAuthKeys(authStore);
  } else {
    enqueueSnackbar({
      message: "Saving token failed: Invalid account ID",
      variant: "error",
    });
  }
};

export const isApolloRESTUnathorized = (code: number | string) => {
  const { FORBIDDEN, UNAUTHORIZED } = HTTP_STATUS_CODE;
  const errorCodes = [99, FORBIDDEN, UNAUTHORIZED];

  return errorCodes.includes(Number(code));
};

export const saveAuthState = async (dispatch: Dispatch<AnyAction>, authData: IAuthLocalStore) => {
  if (!showLoadProductsFromLocalDB()) {
    await clearDefaultLocalForage();
  }
  dispatch(actionAuthenticatingUser());
  const decodedAccessToken = await getDecodedAccessToken(authData?.access_token ?? "");

  const accountType = decodedAccessToken?.type || null;
  const isAllowedToAccessApp = accountType && isAccountTypeAllowed(accountType);

  if (!isAllowedToAccessApp) {
    enqueueSnackbar({
      message: "User Restricted",
      variant: "warning",
    });
    dispatch(actionUnauthorizeUser());
  }

  const authStore: IAuthLocalStore = {
    access_token: authData?.access_token ?? "",
    refresh_token: authData?.refresh_token ?? "",
    store_username: authData?.store_username ?? "",
    user_type: accountType,
  };

  setStorageAuthKeys(authStore);
  authorizeUserInApp(dispatch);

  if (process.env.STAGE === "staging") {
    console.log("Save auth to session storage");
  }
};

export const authorizeUserInApp = (dispatch: Dispatch<AnyAction>) => {
  dispatch(
    actionUpdateAuthorizationReducer({
      isAuthenticated: true,
      isAuthenticating: false,
    }),
  );
};

export const setStorageAuthKeys = (authData: IAuthLocalStore) => {
  saveToSessionStorage<IAuthLocalStore>(LOCAL_STORAGE_AUTH_KEY, authData);
};

export const getStorageAuthKeys = () => {
  const authKeys = getFromSessionStorage<IAuthLocalStore>(LOCAL_STORAGE_AUTH_KEY);
  return authKeys || null;
};

export const getStoreUsernameInAuth = () => {
  const data = getStorageAuthKeys();

  if (data) {
    return data.store_username;
  } else {
    return "";
  }
};

export const getDecodedAccessToken = async (accessToken: string) => {
  interface IDecodedAccessToken {
    type: IAccountTypes;
    role: IAccountRoles;
  }

  try {
    const decodedAccessToken = (await jwtDecode(accessToken)) as IDecodedAccessToken;
    return decodedAccessToken;
  } catch {
    return null;
  }
};

export const isSalespersonToken = (type: IAccountTypes) => {
  return type === IAccountTypes.SALESPERSON;
};

export const isStoreToken = (type: IAccountTypes) => {
  return type === IAccountTypes.STORE;
};

export const isAccountTypeAllowed = (type: IAccountTypes) => {
  return !!IAccountTypes?.[type];
};

export const isAllowedToRefreshToken = async () => {
  try {
    const accessToken = getAccessTokenId();
    const decodedAccessToken = await getDecodedAccessToken(accessToken || "");
    const accType = decodedAccessToken?.type || ("" as IAccountTypes);

    switch (accType) {
      case IAccountTypes.STORE: {
        return true;
      }
      case IAccountTypes.SALESPERSON: {
        return false;
      }
      default: {
        return false;
      }
    }
  } catch {
    return false;
  }
};

export const getAdditionalGQLHeaders = async () => {
  const additionalData: {
    [key: string]: any;
  } = {};

  try {
    const accessToken = getAccessTokenId();
    const decodedAccessToken = await getDecodedAccessToken(accessToken || "");
    const accType = decodedAccessToken?.type;

    switch (accType) {
      case IAccountTypes.SALESPERSON: {
        const storeUsername = getStoreUsernameInAuth();

        if (!!storeUsername) {
          additionalData.store = storeUsername;
        }

        return additionalData;
      }
      case IAccountTypes.STORE: {
        return additionalData;
      }
      default: {
        return additionalData;
      }
    }
  } catch {
    return additionalData;
  }
};
